import React, { useState, useCallback } from 'react';
import { IconButton, Menu, MenuItem, ListItemText, ListItemIcon, Divider } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

/**
 * RowActionsMenu — kebab-icon-button → menu of row-scoped actions.
 *
 * @description
 *   Generic row-actions kebab. Pass an `actions` array; each entry renders as
 *   a MenuItem. Supports per-action `icon`, `divider`, `danger`, `disabled`.
 *   Solves the "9+ existing kebab implementations" sprawl across PO/RMA
 *   tables — refund-lifecycle PR 2 is the first consumer; future tables
 *   should adopt this rather than fork yet another local kebab.
 *
 * @useWhen
 *   - A table row needs 2-N rarely-used actions behind a single icon
 *   - The primary action is already a button on the row; secondary actions
 *     belong in the kebab to keep row visual weight low
 *   - Mobile collapse: actions that would need a dedicated column desktop-side
 *
 * @dontUseFor
 *   - Single-action rows — render the action inline as a button
 *   - Cell-level edit menus inside a Form (use a ConfirmOnSelectDropdown or
 *     ContextMenu instead)
 *   - Page-level action menus (use a Toolbar Button group)
 *
 * @related
 *   - ConfirmOnSelectDropdown — when one of the actions needs inline confirm
 *   - InlineConfirmButton — when the row only has one destructive action
 *
 * @reuseHints
 *   - PO line-items table per-row "Mark as vendor reimbursement" + duplicate + remove
 *   - Recommended Orders rows: "Snooze · Escalate · Convert to PO"
 *   - Vendor inbox rows beyond just "Mark read"
 *
 * @props
 *   actions       — Array<{key, label, icon?, onClick(), divider?, danger?, disabled?, hidden?}>
 *   label         — aria-label for the trigger (default "Row actions")
 *   size          — IconButton size ('small'|'medium', default 'small')
 *   disabled      — disables the kebab entirely
 *   sx            — styles for the trigger IconButton (applied last). Used by a
 *                   caller to restore the 44-px floor on a coarse pointer inside
 *                   a table cell, where the theme deliberately drops it
 *                   (G5b-Nachbesserung 1, R16 W1)
 *
 *   The trigger announces its target: `aria-haspopup="menu"` and
 *   `aria-expanded` (ARIA-APG form for a menu). Both are additive — MUI's `Menu`
 *   does not set them on the anchor.
 */
export default function RowActionsMenu({
  actions = [],
  label = 'Row actions',
  size = 'small',
  disabled = false,
  sx,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = !!anchorEl;

  const handleOpen = useCallback((e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback((e) => {
    if (e?.stopPropagation) e.stopPropagation();
    setAnchorEl(null);
  }, []);

  const visible = actions.filter((a) => !a.hidden);
  if (visible.length === 0) return null;

  return (
    <>
      <IconButton
        aria-label={label}
        // 🔴 ARIA-APG-Form für ein Menü (R15 W7): der Auslöser sagt an, DASS er
        // ein Menü öffnet, und ob es offen ist. Ohne die zwei Attribute ist der
        // Knopf ein namenloser „button" mit einem Namen — die Vorlesehilfe
        // erfährt nichts über das Ziel. `MUI Menu` setzt sie am ANKER nicht.
        aria-haspopup="menu"
        aria-expanded={open}
        size={size}
        onClick={handleOpen}
        disabled={disabled}
        sx={sx}
      >
        <MoreVertIcon fontSize={size === 'small' ? 'small' : 'medium'} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
      >
        {visible.map((a, idx) => {
          const item = (
            <MenuItem
              key={a.key || a.label || idx}
              disabled={!!a.disabled}
              onClick={(e) => {
                handleClose(e);
                if (typeof a.onClick === 'function') a.onClick(e);
              }}
              sx={a.danger ? { color: 'error.main' } : undefined}
            >
              {a.icon && <ListItemIcon sx={a.danger ? { color: 'error.main' } : undefined}>{a.icon}</ListItemIcon>}
              <ListItemText primary={a.label} />
            </MenuItem>
          );
          return a.divider ? (
            <React.Fragment key={`${a.key || a.label || idx}-with-div`}>
              <Divider />
              {item}
            </React.Fragment>
          ) : item;
        })}
      </Menu>
    </>
  );
}
